// Voice over WebRTC — peer-to-peer entre dos tripulantes sellados.
// El servidor solo enruta SDP + ICE; el audio nunca pasa por él.
//
// Una sola conexión por enlace. La cierra `closeVoiceConnection()` cuando
// el sello se rompe (LINK_ABORTED / desconexión).
//
// 2026-05-06 — Fixes para iOS Safari (audio P2P silencioso):
//   1. `playsInline` + `webkit-playsinline` en el <audio> remoto. Sin
//      esto iOS Safari trata al elemento como video fullscreen y
//      bloquea su pipeline de audio.
//   2. Llamada explícita a `el.play()` con reintentos (autoplay no
//      siempre arranca el decoder en iOS).
//   3. `el.muted = false` defensivo.
//
// 2026-05-07 — Fix de race condition del setup:
//   Cuando ambos lados sellan al mismo tiempo, `openVoiceConnection`
//   arranca en paralelo y `getUserMedia` puede tardar más en uno que
//   en el otro (iOS especialmente, pide permiso). Si el iniciador
//   termina antes y manda la OFFER, el receptor recibe `WEBRTC_OFFER`
//   cuando todavía está esperando su `getUserMedia` → en ese momento
//   `active` es null y la offer se descartaba silenciosamente. La
//   conexión nunca se establecía, los meters quedaban planos y nadie
//   se escuchaba. Síntoma: "primer enlace no funciona, segundo sí"
//   (porque la segunda vez el permiso ya está dado y getUserMedia es
//   instantáneo, no hay race).
//
//   Fix: exponemos una promesa `inflightSetup` que se resuelve cuando
//   `active` ya existe. Los handlers de OFFER/ANSWER/ICE esperan esa
//   promesa antes de procesar — si llega temprano, se cuelga; si
//   llega tarde, pasa directo. Y un buffer de ICE candidates por si
//   llegan antes de `setRemoteDescription` (Safari es estricto al
//   respecto).

import { SOCKET_EVENTS } from "@domo/shared"
import type { Socket } from "socket.io-client"

const ICE_SERVERS: RTCIceServer[] = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
]

interface VoiceConnection {
    pc: RTCPeerConnection
    localStream: MediaStream
    remoteStream: MediaStream | null
    remoteAudioEl: HTMLAudioElement
    peerId: string
    /** Si todavía no hicimos setRemoteDescription, los ICE
     *  candidates se almacenan acá y se drenan después. Safari es
     *  estricto: addIceCandidate antes de setRemoteDescription falla. */
    pendingIce: RTCIceCandidateInit[]
    /** True una vez que setRemoteDescription se ejecutó. */
    remoteDescriptionSet: boolean
    cleanup: () => void
}

let active: VoiceConnection | null = null
// Promesa que resuelve cuando `active` ya está poblado. Los handlers
// de signaling la esperan si llegan antes del setup.
let inflightSetup: Promise<VoiceConnection | null> | null = null

export function openVoiceConnection(opts: {
    socket: Socket
    peerId: string
    isInitiator: boolean
}): Promise<VoiceConnection | null> {
    closeVoiceConnection()
    inflightSetup = setupVoiceConnection(opts)
    return inflightSetup
}

async function setupVoiceConnection(opts: {
    socket: Socket
    peerId: string
    isInitiator: boolean
}): Promise<VoiceConnection | null> {
    let localStream: MediaStream
    try {
        localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
            video: false,
        })
    } catch (err) {
        console.warn("[domo] mic denied:", err)
        return null
    }

    const localAudioTracks = localStream.getAudioTracks()
    if (localAudioTracks.length === 0) {
        console.warn("[domo] mic stream sin audio tracks")
    } else {
        const t = localAudioTracks[0]
        console.log(
            `[domo] mic captured: track="${t.label}" enabled=${t.enabled} muted=${t.muted} state=${t.readyState}`
        )
        t.onmute = () => console.log("[domo] mic track muted")
        t.onunmute = () => console.log("[domo] mic track UNmuted")
        t.onended = () => console.log("[domo] mic track ended")
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })

    for (const track of localStream.getTracks()) {
        pc.addTrack(track, localStream)
    }

    const remoteAudioEl = document.createElement("audio")
    remoteAudioEl.autoplay = true
    remoteAudioEl.muted = false
    remoteAudioEl.setAttribute("playsinline", "")
    remoteAudioEl.setAttribute("webkit-playsinline", "")
    remoteAudioEl.dataset.domoPeer = opts.peerId
    document.body.appendChild(remoteAudioEl)

    pc.ontrack = (ev) => {
        const [stream] = ev.streams
        if (!stream) {
            console.warn("[domo] ontrack sin streams")
            return
        }
        const rTracks = stream.getAudioTracks()
        console.log(
            `[domo] ontrack remote: kind=${ev.track.kind} muted=${ev.track.muted} enabled=${ev.track.enabled} state=${ev.track.readyState} (${rTracks.length} audio tracks)`
        )
        ev.track.onmute = () => console.log("[domo] remote track muted")
        ev.track.onunmute = () =>
            console.log("[domo] remote track UNmuted")
        ev.track.onended = () => console.log("[domo] remote track ended")
        remoteAudioEl.srcObject = stream
        if (active) active.remoteStream = stream
        const tryPlay = (attempt = 0) => {
            remoteAudioEl
                .play()
                .then(() => {
                    if (attempt > 0) {
                        console.log(
                            `[domo] remote audio play OK tras ${attempt} reintentos`
                        )
                    }
                })
                .catch((err) => {
                    if (attempt < 6) {
                        window.setTimeout(
                            () => tryPlay(attempt + 1),
                            250
                        )
                    } else {
                        console.warn(
                            "[domo] remote audio play() rechazado por el browser:",
                            err
                        )
                    }
                })
        }
        tryPlay()
    }

    pc.onicecandidate = (ev) => {
        if (ev.candidate) {
            opts.socket.emit(SOCKET_EVENTS.WEBRTC_ICE, {
                to: opts.peerId,
                candidate: ev.candidate.toJSON(),
            })
        }
    }

    pc.onconnectionstatechange = () => {
        // eslint-disable-next-line no-console
        console.log(`[domo] WebRTC state: ${pc.connectionState}`)
        if (pc.connectionState === "failed") {
            console.warn("[domo] WebRTC connection failed")
        }
    }

    if (opts.isInitiator) {
        const offer = await pc.createOffer({ offerToReceiveAudio: true })
        await pc.setLocalDescription(offer)
        opts.socket.emit(SOCKET_EVENTS.WEBRTC_OFFER, {
            to: opts.peerId,
            sdp: offer,
        })
    }

    const cleanup = () => {
        try {
            for (const track of localStream.getTracks()) track.stop()
            pc.close()
            remoteAudioEl.srcObject = null
            remoteAudioEl.remove()
        } catch {}
    }

    active = {
        pc,
        localStream,
        remoteStream: null,
        remoteAudioEl,
        peerId: opts.peerId,
        pendingIce: [],
        remoteDescriptionSet: false,
        cleanup,
    }
    console.log(
        `[domo] voice setup ready (peer=${opts.peerId} initiator=${opts.isInitiator})`
    )
    return active
}

/** Espera al setup en vuelo si el handler arrancó antes de que
 *  `active` esté poblado. Devuelve `active` o null si el setup falló. */
async function ensureActive(
    expectedPeerId: string
): Promise<VoiceConnection | null> {
    if (active && active.peerId === expectedPeerId) return active
    if (inflightSetup) {
        try {
            const a = await inflightSetup
            if (a && a.peerId === expectedPeerId) return a
        } catch {}
    }
    return active && active.peerId === expectedPeerId ? active : null
}

export async function handleIncomingOffer(
    socket: Socket,
    from: string,
    sdp: RTCSessionDescriptionInit
): Promise<void> {
    const conn = await ensureActive(from)
    if (!conn) {
        console.warn(
            `[domo] offer de ${from} sin active connection — descartado`
        )
        return
    }
    try {
        await conn.pc.setRemoteDescription(sdp)
        conn.remoteDescriptionSet = true
        await drainPendingIce(conn)
        const answer = await conn.pc.createAnswer()
        await conn.pc.setLocalDescription(answer)
        socket.emit(SOCKET_EVENTS.WEBRTC_ANSWER, { to: from, sdp: answer })
        console.log(`[domo] answer enviado a ${from}`)
    } catch (err) {
        console.warn("[domo] handleIncomingOffer falló:", err)
    }
}

export async function handleIncomingAnswer(
    from: string,
    sdp: RTCSessionDescriptionInit
): Promise<void> {
    const conn = await ensureActive(from)
    if (!conn) return
    if (conn.pc.signalingState === "stable") {
        // Algunos browsers terminan en stable si la answer ya fue
        // procesada — log y salir sin reaplicar.
        console.log(
            `[domo] answer recibida con signalingState=stable — skip`
        )
        return
    }
    try {
        await conn.pc.setRemoteDescription(sdp)
        conn.remoteDescriptionSet = true
        await drainPendingIce(conn)
    } catch (err) {
        console.warn("[domo] handleIncomingAnswer falló:", err)
    }
}

export async function handleIncomingIce(
    from: string,
    candidate: RTCIceCandidateInit
): Promise<void> {
    const conn = await ensureActive(from)
    if (!conn) return
    if (!conn.remoteDescriptionSet) {
        // Safari es estricto: addIceCandidate antes de
        // setRemoteDescription falla. Encolamos para drenar
        // después.
        conn.pendingIce.push(candidate)
        return
    }
    try {
        await conn.pc.addIceCandidate(candidate)
    } catch (err) {
        console.warn("[domo] ice add failed:", err)
    }
}

async function drainPendingIce(conn: VoiceConnection): Promise<void> {
    if (conn.pendingIce.length === 0) return
    const pending = conn.pendingIce.splice(0)
    console.log(
        `[domo] drenando ${pending.length} ICE candidates encolados`
    )
    for (const c of pending) {
        try {
            await conn.pc.addIceCandidate(c)
        } catch (err) {
            console.warn("[domo] pending ice add failed:", err)
        }
    }
}

export function closeVoiceConnection(): void {
    if (!active) return
    active.cleanup()
    active = null
    inflightSetup = null
}

export function getActiveVoicePeer(): string | null {
    return active?.peerId ?? null
}

export function getRemoteStream(): MediaStream | null {
    return active?.remoteStream ?? null
}

export function getLocalStream(): MediaStream | null {
    return active?.localStream ?? null
}

export function setMicEnabled(enabled: boolean): void {
    if (!active) return
    for (const track of active.localStream.getAudioTracks()) {
        track.enabled = enabled
    }
}
